<script>
import {
  fetchFormList,
  createForm,
  updateForm,
  deleteForm,
  fetchFields,
  fetchForm,
  createField,
  updateField,
  fetchField,
  deleteField,
} from '@apis/admin/form'
import LangInput from '@components/admin/lang-input'
import { mapState } from 'vuex'
import fromPairs from 'lodash/fromPairs'
import OptionsetEditor from '@components/admin/optionset-editor'

export default {
  components: { LangInput, OptionsetEditor },
  props: {
    target_type: {
      type: String,
      default: '',
    },
    target_seq: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      forms: [],
      loading: false,
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      this.loading = true
      Promise.all([this.loadForms()])
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadForms() {
      const { data } = await fetchFormList({
        ...this.$props,
        no_paging: 1,
      })

      this.forms = data
      await Promise.all(this.forms.map(this.mapFormFields))
    },
    async mapFormFields(form) {
      const { data } = await fetchFields(form.seq)

      this.$set(form, 'fields', data)
    },
    handleCreateForm() {
      this.forms.push({
        seq: null,
        title: '',
        description: '',
        target_type: this.target_type,
        target_seq: this.target_seq,
        list_order: null,
        fields: [],
      })
    },
    handleCreateField(form) {
      form.fields.push({
        seq: null,
        form_seq: form.seq,
        name: '',
        title: '',
        field_type: null,
        description: '',
        placeholder: '',
        list_order: null,
        is_editable: 'Y',
        is_required: 'N',
      })
    },
    async handleDeleteForm(idx) {
      this.loading = true
      try {
        if (this.forms[idx].seq) {
          if (
            !(await this.$confirm(
              this.$t('admin.confirm_delete'),
              this.$t('admin.modal_confirm'),
              {
                type: 'warning',
              }
            ).catch(() => false))
          )
            return

          await deleteForm(this.forms[idx].seq)
        }

        this.forms.splice(idx, 1)
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    async handleDeleteField(form, idx) {
      this.loading = true
      try {
        if (form.fields[idx].seq) {
          if (
            !(await this.$confirm(
              this.$t('admin.confirm_delete'),
              this.$t('admin.modal_confirm'),
              {
                type: 'warning',
              }
            ).catch(() => false))
          )
            return

          await deleteField(form.seq, form.fields[idx].seq)
        }

        form.fields.splice(idx, 1)
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    handleSubmitForm(idx) {
      this.loading = true
      const params = this.forms[idx]
      ;(params.seq ? updateForm : createForm)(params)
        .then(({ seq }) => fetchForm(seq))
        .then(({ form_info }) => {
          this.$set(this.forms, idx, { ...form_info, fields: params.fields })
        })
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    handleSubmitField(form, idx) {
      this.loading = true
      const params = form.fields[idx]
      ;(params.seq ? updateField : createField)(params)
        .then(({ seq }) => fetchField(form.seq, seq))
        .then(({ field_info }) => {
          this.$set(form.fields, idx, field_info)
        })
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    fromPairs,
  },
}
</script>

<template>
  <div v-loading="loading" :class="$style.clearfix">
    <fieldset :class="[$style.fr]">
      <a
        href="#"
        :class="[$style.black, $style.button]"
        @click="handleCreateForm"
        ><span>{{ $t('admin.form_info_add') }}</span></a
      >
    </fieldset>
    <div
      v-for="(form_info, idx) in forms"
      :key="form_info.seq || `create-${idx}`"
    >
      <h3 :class="[$style.xeAdmin, $style.clear, $style.clearfix]">
        <LangInput
          v-model="form_info.title"
          :placeholder="$t('admin.form_info_title')"
          :class="[$style.w400, $style.fl]"
        ></LangInput>

        <div :class="$style.fr">
          <ElInput v-model="form_info.list_order">
            <template slot="prepend">{{
              $t('admin.form_info_list_order')
            }}</template>
            <template slot="append">
              <ElButton
                :class="[$style.fl]"
                type="danger"
                icon="el-icon-delete"
                circle
                @click="handleDeleteForm(idx)"
              ></ElButton>
              <ElButton
                type="success"
                icon="el-icon-check"
                circle
                @click="handleSubmitForm(idx)"
              ></ElButton>
            </template>
          </ElInput>
        </div>
      </h3>
      <div :class="$style.infoText">
        <p>
          <LangInput
            v-model="form_info.description"
            :placeholder="$t('admin.form_info_description')"
          ></LangInput>
        </p>
      </div>
      <table
        v-if="form_info.seq"
        cellspacing="0"
        cellpadding="0"
        :class="$style.colTable"
      >
        <thead>
          <tr>
            <th scope="col" rowspan="2"
              ><div>{{ $t('admin.form_info_list_order') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('admin.field_info_name') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('admin.field_info_title') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('admin.field_info_description') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('admin.field_info_is_editable') }}</div></th
            >
            <th scope="col" rowspan="2">
              <div :class="$style.tRight">
                <a
                  href="#"
                  :class="[$style.black, $style.button]"
                  @click="handleCreateField(form_info)"
                  ><span>{{ $t('admin.add') }}</span></a
                >
              </div>
            </th>
          </tr>
          <tr>
            <th scope="col"
              ><div>{{ $t('admin.field_info_type') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('admin.field_info_select') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('admin.field_info_no_space') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('admin.field_info_is_required') }}</div></th
            >
          </tr>
        </thead>
        <tbody v-if="form_info.fields">
          <template v-for="(field_info, field_idx) in form_info.fields">
            <tr
              :key="
                field_info.seq ? `1-${field_info.seq}` : `1-create-${field_idx}`
              "
              :class="$style[`bg${field_idx % 2}`]"
            >
              <td rowspan="2" :class="$style.tCenter"
                ><input
                  v-model="field_info.list_order"
                  :class="[$style.inputTypeText, $style.w40]"
                  type="text"
              /></td>
              <td :class="$style.tCenter"
                ><input
                  v-model="field_info.name"
                  :class="[$style.inputTypeText, $style.fullWidth]"
                  type="text"
              /></td>
              <td
                ><LangInput
                  v-model="field_info.title"
                  :class="$style.wide"
                ></LangInput
              ></td>
              <td><LangInput v-model="field_info.description"></LangInput></td>
              <td :class="$style.tCenter"
                ><ElSwitch
                  v-model="field_info.is_editable"
                  active-value="Y"
                  inactive-value="N"
                ></ElSwitch
              ></td>
              <td rowspan="2" :class="[$style.tRight, $style.nowrap]">
                <a
                  v-if="field_info.seq"
                  href="#"
                  :class="[$style.button]"
                  @click="handleSubmitField(form_info, field_idx)"
                  ><span>{{ $t('admin.modify') }}</span></a
                >
                <a
                  v-else
                  href="#"
                  :class="[$style.black, $style.button]"
                  @click="handleSubmitField(form_info, field_idx)"
                  ><span>{{ $t('admin.enroll') }}</span></a
                >
                {{ ' ' }}
                <a
                  href="#"
                  :class="[$style.red, $style.button]"
                  @click="handleDeleteField(form_info, field_idx)"
                  ><span>{{ $t('admin.delete') }}</span></a
                >
              </td>
            </tr>
            <tr
              :key="
                field_info.seq ? `2-${field_info.seq}` : `2-create-${field_idx}`
              "
              :class="$style[`bg${field_idx % 2}`]"
            >
              <td :class="$style.tCenter">
                <template v-if="field_info.seq">{{
                  fromPairs(constants.form.field_type)[field_info.field_type]
                }}</template>
                <select v-else v-model="field_info.field_type">
                  <option :value="null">{{ $t('select') }}</option>
                  <option
                    v-for="val in constants.form.field_type"
                    :key="val[1]"
                    :value="val[0]"
                    >{{ val[1] }}</option
                  >
                </select>
              </td>
              <td :class="$style.tCenter">
                <OptionsetEditor
                  v-if="
                    ['RADIO', 'SELECT', 'CHECKBOX', 'MULTISELECT'].indexOf(
                      field_info.field_type
                    ) !== -1
                  "
                  v-model="field_info.optionset_seq"
                ></OptionsetEditor>
                <template
                  v-else-if="['FILE'].indexOf(field_info.field_type) !== -1"
                >
                  <input
                    v-model="field_info.extension"
                    :class="[$style.inputTypeText, $style.fullWidth]"
                    :placeholder="$t('admin.field_info_extension_place')"
                    type="text"
                  />
                </template>
                <template v-else>
                  {{ $t('admin.no_target') }}
                </template> </td
              ><td><LangInput v-model="field_info.placeholder"></LangInput></td>
              <td :class="$style.tCenter"
                ><ElSwitch
                  v-model="field_info.is_required"
                  active-value="Y"
                  inactive-value="N"
                ></ElSwitch
              ></td>
            </tr>
          </template>
          <tr v-if="!form_info.fields.length">
            <td colspan="6" :class="$style.tCenter">{{
              $t('admin.no_field')
            }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';

.colTable th,
.colTable td {
  vertical-align: middle;
}
</style>
